// src/pages/PromotionGiftHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./PromotionGiftHistory.css";
import "./PromotionGiftHistory.sale.css";

export default function PromotionGiftHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const promoIdFromUrl = searchParams.get("promoId") || "";

  const [coupons, setCoupons] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [issuedShopFilter, setIssuedShopFilter] = useState("ALL");
  const [usedShopFilter, setUsedShopFilter] = useState("ALL");
  const [saleGifts, setSaleGifts] = useState([]);
  const [saleGiftLoading, setSaleGiftLoading] = useState(false);
  const [saleGiftError, setSaleGiftError] = useState("");
  const [saleGiftSearch, setSaleGiftSearch] = useState("");
  const [saleGiftStatus, setSaleGiftStatus] = useState("GIVEN");

  useEffect(() => {
    let active = true;
    if (!promoIdFromUrl) {
      setSaleGifts([]);
      return undefined;
    }
    setSaleGiftLoading(true);
    setSaleGiftError("");
    fetch(`${API_BASE}/api/erp/pos/promotion-gift-vouchers/${encodeURIComponent(promoIdFromUrl)}`, { cache: "no-store" })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || "Gift history could not be loaded");
        return data;
      })
      .then((data) => {
        if (active) setSaleGifts(Array.isArray(data.rows) ? data.rows : []);
      })
      .catch((error) => {
        if (active) {
          setSaleGifts([]);
          setSaleGiftError(error.message || "Gift history could not be loaded");
        }
      })
      .finally(() => { if (active) setSaleGiftLoading(false); });
    return () => { active = false; };
  }, [promoIdFromUrl]);

  useEffect(() => {
    const loadGiftHistory = async () => {
      if (!promoIdFromUrl) {
        setCoupons([]);
        return;
      }

      try {
        const res = await fetch(
          `${API_BASE}/api/promotion-gift-history/${encodeURIComponent(
            promoIdFromUrl,
          )}`,
        );

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json?.success) {
          console.error("Gift history fetch failed:", json);
          setCoupons([]);
          return;
        }
console.log("✅ Gift history rows from VPS:", json.rows || []);
        const giftVoucherRows = Array.isArray(json.rows) ? json.rows : [];

        // ERP_GIFT_ITEM_SESSION_HISTORY_V2
        // Gift Item promotion တွေက voucher code မထုတ်ဘဲ POS promo session ထဲမှာသိမ်းထားတယ်။
        // အဲ့ session rows တွေကို coupons list ထဲ merge လုပ်ပြီး ဒီ history table မှာပြမယ်။
        let giftItemSessionRows = [];

        try {
          const sessionParams = new URLSearchParams();
          sessionParams.set("promotionId", promoIdFromUrl);
          sessionParams.set("limit", "200");

          const sessionRes = await fetch(
            `${API_BASE}/api/erp/pos/promo-sessions?${sessionParams.toString()}`,
            { cache: "no-store" },
          );

          const sessionJson = await sessionRes.json().catch(() => ({}));
          const sessions = Array.isArray(sessionJson.sessions)
            ? sessionJson.sessions
            : [];

          giftItemSessionRows = sessions
            .map((session) => {
              const gift =
                session?.cartSnapshot?.giftItem ||
                session?.saleSnapshot?.promotionGiftItem ||
                null;

              if (!gift) return null;

              const status = String(session.status || "").toUpperCase();
              const used = status === "AUTO_FINALIZED";

              const promoId =
                session.promotionId ||
                gift.promoId ||
                promoIdFromUrl ||
                "";

              const giftCode =
                gift.giftItemCode ||
                gift.code ||
                gift.giftPresetKey ||
                "GIFT_ITEM";

              const giftName =
                gift.giftItemName ||
                gift.name ||
                giftCode ||
                "Promotion Gift Item";

              const customerName =
                session?.saleSnapshot?.customer?.name ||
                session?.saleSnapshot?.customerName ||
                "";

              const customerPhone =
                session?.saleSnapshot?.customer?.phone ||
                session?.saleSnapshot?.memberPhone ||
                session?.saleSnapshot?.customerPhone ||
                "";

              return {
                id: `promo-session-${session.id}`,
                _key: `promo-session-${session.id}`,
                code: giftCode,

                promotionId: promoId,
                promoId: promoId,
                sourcePromotionId: promoId,
                sourcePromotionName:
                  gift.promotionName || promoId || "Gift Item Promotion",

                amount: 0,
                giftAmount: 0,
                giftItemCode: giftCode,
                giftItemName: giftName,
                giftQty: Number(gift.giftQty || gift.qty || 1),

                expireDate: session.expiresAt || "",
                issuedByShop: session.shopCode || "",
                issuedFromVoucherNo: session.voucherNo || "",
                sourceVoucherNo: session.voucherNo || "",
                issuedAt: session.startedAt || session.createdAt || "",
                issuedCustomerName: customerName,
                issuedCustomerPhone: customerPhone,

                used,
                usedByShop: used ? session.shopCode || "" : "",
                usedVoucherNo: used ? session.voucherNo || "" : "",
                voucherNo: used ? session.voucherNo || "" : "",
                usedAt: used ? session.finalizedAt || session.updatedAt || "" : "",
                usedCustomerName: used ? customerName : "",
                usedCustomerPhone: used ? customerPhone : "",

                __giftItemSessionHistory: true,
                __sessionStatus: session.status || "",
                __sessionId: session.id,
              };
            })
            .filter(Boolean);
        } catch (sessionErr) {
          console.warn("Gift item session history fetch failed:", sessionErr);
        }

        console.log("✅ Gift voucher rows:", giftVoucherRows);
        console.log("✅ Gift item session rows:", giftItemSessionRows);

        setCoupons([...giftItemSessionRows, ...giftVoucherRows]);
      } catch (err) {
        console.error("Gift history fetch error:", err);
        setCoupons([]);
      }
    };

    loadGiftHistory();
  }, [promoIdFromUrl]);
  const formatDateTime = (value) => {
    if (!value) return "-";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isExpired = (expireDate) => {
    if (!expireDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const exp = new Date(expireDate);
    exp.setHours(23, 59, 59, 999);

    return exp < today;
  };

 const giftCoupons = useMemo(() => {
  if (!promoIdFromUrl) return [];

  return (coupons || []).filter((c) => {
    const promotionId =
      c.promotionId ||
      c.sourcePromotionId ||
      c.promoId ||
      "";

    return (
      String(promotionId).trim().toUpperCase() ===
      String(promoIdFromUrl).trim().toUpperCase()
    );
  });
}, [coupons, promoIdFromUrl]);

  const issuedShops = useMemo(() => {
    const shops = giftCoupons
      .map((c) => c.issuedByShop)
      .filter(Boolean)
      .map((s) => String(s).toUpperCase());

    return ["ALL", ...Array.from(new Set(shops))];
  }, [giftCoupons]);

  const usedShops = useMemo(() => {
    const shops = giftCoupons
      .map((c) => c.usedByShop)
      .filter(Boolean)
      .map((s) => String(s).toUpperCase());

    return ["ALL", ...Array.from(new Set(shops))];
  }, [giftCoupons]);

  const filteredCoupons = useMemo(() => {
    const q = search.trim().toUpperCase();

    return giftCoupons.filter((c) => {
      const code = c._key || c.code || "";
      const used = c.used === true;
      const expired = isExpired(c.expireDate);

      if (statusFilter === "USED" && !used) return false;
      if (statusFilter === "UNUSED" && used) return false;
      if (statusFilter === "EXPIRED" && !expired) return false;

      if (
        issuedShopFilter !== "ALL" &&
        String(c.issuedByShop || "").toUpperCase() !== issuedShopFilter
      ) {
        return false;
      }

      if (
        usedShopFilter !== "ALL" &&
        String(c.usedByShop || "").toUpperCase() !== usedShopFilter
      ) {
        return false;
      }

      if (!q) return true;

      const text = [
        code,
        c.sourcePromotionId,
        c.sourcePromotionName,
        c.issuedByShop,
        c.issuedFromVoucherNo,
        c.sourceVoucherNo,
        c.issuedCustomerName,
        c.issuedCustomerPhone,
        c.usedByShop,
        c.usedVoucherNo,
        c.voucherNo,
        c.usedCustomerName,
        c.usedCustomerPhone,
        used ? "USED" : "UNUSED",
      ]
        .join(" ")
        .toUpperCase();

      return text.includes(q);
    });
  }, [giftCoupons, search, statusFilter, issuedShopFilter, usedShopFilter]);

  const summary = useMemo(() => {
    const totalIssued = giftCoupons.length;
    const used = giftCoupons.filter((c) => c.used === true).length;
    const unused = giftCoupons.filter((c) => c.used !== true).length;
    const expired = giftCoupons.filter(
      (c) => c.used !== true && isExpired(c.expireDate),
    ).length;

    return {
      totalIssued,
      used,
      unused,
      expired,
    };
  }, [giftCoupons]);

  const saleGiftSummary = useMemo(() => {
    const given = saleGifts.filter((gift) => gift.status === "GIVEN");
    return {
      vouchers: new Set(given.map((gift) => gift.voucherNo)).size,
      items: given.reduce((total, gift) => total + Number(gift.giftQty || 0), 0),
      declined: saleGifts.filter((gift) => gift.status === "NOT_GIVEN").length,
    };
  }, [saleGifts]);

  const filteredSaleGifts = useMemo(() => {
    const query = saleGiftSearch.trim().toUpperCase();
    return saleGifts.filter((gift) => {
      if (saleGiftStatus !== "ALL" && gift.status !== saleGiftStatus) return false;
      return !query || [gift.voucherNo, gift.shopCode, gift.giftCode, gift.giftName]
        .some((value) => String(value || "").toUpperCase().includes(query));
    });
  }, [saleGifts, saleGiftSearch, saleGiftStatus]);

  const showCouponHistory = saleGifts.length === 0 || giftCoupons.some((coupon) => !coupon.__giftItemSessionHistory);

  const titleText = promoIdFromUrl
    ? `Promotion History - ${promoIdFromUrl}`
    : "Promotion History";

  return (
    <Layout title="Promotion History">
      <div className="gift-history-page">
        <div className="gift-history-header">
          <div>
            <h2>{titleText}</h2>
            <p>Sale voucher တစ်ခုချင်းစီမှာ ပေးခဲ့တဲ့ gift နဲ့ gift voucher history ကိုကြည့်ရန်။</p>
          </div>

          <button
            type="button"
            className="gift-back-btn"
            onClick={() => navigate("/erp/promotions")}
          >
            Back to Promotions
          </button>
        </div>

        {(saleGifts.length > 0 || saleGiftLoading || saleGiftError) && (
          <section className="gift-sale-section">
            <h3>POS Voucher Gifts</h3>
            <p>ပေးပြီးသား gift များကို အောက်မှာ voucher အလိုက် ပြထားပါတယ်။ ဝယ်သူမယူခဲ့သော gift များကို Status မှ ရွေးကြည့်နိုင်ပါတယ်။</p>
            <div className="gift-sale-summary">
              <span>Gift ရထားသော Voucher <strong>{saleGiftSummary.vouchers}</strong></span>
              <span>ပေးခဲ့သော Gift အရေအတွက် <strong>{saleGiftSummary.items}</strong></span>
              <span>ဝယ်သူမယူခဲ့ <strong>{saleGiftSummary.declined}</strong></span>
            </div>
            <div className="gift-sale-filters">
              <input value={saleGiftSearch} onChange={(event) => setSaleGiftSearch(event.target.value)} placeholder="Voucher / Shop / Gift ရှာရန်" aria-label="Search POS voucher gifts" />
              <select value={saleGiftStatus} onChange={(event) => setSaleGiftStatus(event.target.value)} aria-label="POS gift status">
                <option value="GIVEN">ပေးပြီးသား Gift</option>
                <option value="NOT_GIVEN">ဝယ်သူမယူခဲ့</option>
                <option value="PENDING">စစ်ဆေးရန်</option>
                <option value="ALL">အားလုံး</option>
              </select>
            </div>
            {saleGiftError && <p className="gift-sale-error">{saleGiftError}</p>}
            <div className="gift-table-wrap">
              <table className="gift-table gift-sale-table">
                <thead><tr><th>Sale Date</th><th>Shop</th><th>Voucher</th><th>Gift Item</th><th>Qty</th><th>Status</th></tr></thead>
                <tbody>
                  {filteredSaleGifts.length ? filteredSaleGifts.map((gift, index) => (
                    <tr key={`${gift.voucherNo}-${gift.giftCode}-${index}`}>
                      <td>{formatDateTime(gift.saleAt)}</td>
                      <td>{gift.shopCode || "-"}</td>
                      <td className="gift-code">{gift.voucherNo || "-"}</td>
                      <td>{gift.giftName || gift.giftCode || "-"}{gift.giftCode && gift.giftName !== gift.giftCode ? ` (${gift.giftCode})` : ""}</td>
                      <td>{gift.giftQty}</td>
                      <td><span className={`gift-status ${gift.status === "GIVEN" ? "used" : gift.status === "NOT_GIVEN" ? "expired" : "unused"}`}>
                        {gift.status === "GIVEN" ? "ပေးပြီး" : gift.status === "NOT_GIVEN" ? "မယူခဲ့" : "စစ်ဆေးရန်"}
                      </span></td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="gift-empty">{saleGiftLoading ? "Loading..." : "ရွေးထားတဲ့ Status အတွက် voucher မရှိပါ"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {showCouponHistory && <>
        <h3>Gift Voucher History</h3>
        <div className="gift-summary-grid">
          <div className="gift-summary-card">
            <span>Total Issued</span>
            <strong>{summary.totalIssued}</strong>
          </div>

          <div className="gift-summary-card">
            <span>Used</span>
            <strong>{summary.used}</strong>
          </div>

          <div className="gift-summary-card">
            <span>Unused</span>
            <strong>{summary.unused}</strong>
          </div>

          <div className="gift-summary-card">
            <span>Expired</span>
            <strong>{summary.expired}</strong>
          </div>
        </div>

        <div className="gift-filter-card">
          <input
            className="gift-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code / phone / name / voucher..."
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="USED">Used</option>
            <option value="UNUSED">Unused</option>
            <option value="EXPIRED">Expired</option>
          </select>

          <select
            value={issuedShopFilter}
            onChange={(e) => setIssuedShopFilter(e.target.value)}
          >
            {issuedShops.map((shop) => (
              <option key={shop} value={shop}>
                Issued Shop: {shop}
              </option>
            ))}
          </select>

          <select
            value={usedShopFilter}
            onChange={(e) => setUsedShopFilter(e.target.value)}
          >
            {usedShops.map((shop) => (
              <option key={shop} value={shop}>
                Used Shop: {shop}
              </option>
            ))}
          </select>
        </div>

        <div className="gift-table-wrap">
          <table className="gift-table">
            <thead>
              <tr>
                <th>Gift Code</th>
                <th>Promotion</th>
                <th>Amount</th>
                <th>Expire</th>

                <th>Issued Shop</th>
                <th>Issued Voucher</th>
                <th>Issued At</th>
                <th>Issued Customer</th>
                <th>Issued Phone</th>

                <th>Status</th>
                <th>Used Shop</th>
                <th>Used Voucher</th>
                <th>Used At</th>
                <th>Used Customer</th>
                <th>Used Phone</th>
              </tr>
            </thead>

            <tbody>
              {filteredCoupons.length === 0 ? (
                <tr>
                  <td colSpan="15" className="gift-empty">
                    No gift voucher history found
                  </td>
                </tr>
              ) : (
                filteredCoupons.map((c) => {
                  const code = c.code || c._key || c.id || "-";
                  const used = c.used === true;
                  const expired = isExpired(c.expireDate);

                  const promotionName =
                    c.promotionName ||
                    c.sourcePromotionName ||
                    c.promoName ||
                    "-";

                  const promotionId =
                    c.promotionId || c.sourcePromotionId || c.promoId || "-";

                  const issuedVoucherNo =
                    c.issuedFromVoucherNo ||
                    c.sourceVoucherNo ||
                    c.voucherNo ||
                    "-";

                  const usedVoucherNo = c.usedVoucherNo || c.voucherNo || "-";

                  return (
                    <tr key={c.id || c._key || c.code}>
                      <td className="gift-code">{code}</td>

                      <td>
                        <div className="gift-promo-name">{promotionName}</div>
                        <div className="gift-promo-id">{promotionId}</div>
                      </td>

                      <td>{Number(c.amount || 0).toLocaleString()} Ks</td>
                      <td>{c.expireDate || "-"}</td>

                      <td>
                        <span className="gift-badge shop">
                          {c.issuedByShop || c.shopId || "-"}
                        </span>
                      </td>

                      <td>{issuedVoucherNo}</td>
                      <td>{formatDateTime(c.issuedAt || c.createdAt)}</td>
                      <td>{c.issuedCustomerName || c.customerName || "-"}</td>
                      <td>{c.issuedCustomerPhone || c.customerPhone || "-"}</td>

                      <td>
                        {used ? (
                          <span className="gift-status used">Used</span>
                        ) : expired ? (
                          <span className="gift-status expired">Expired</span>
                        ) : (
                          <span className="gift-status unused">Unused</span>
                        )}
                      </td>

                      <td>
                        <span className="gift-badge used-shop">
                          {c.usedByShop || "-"}
                        </span>
                      </td>

                      <td>{usedVoucherNo}</td>
                      <td>{formatDateTime(c.usedAt)}</td>
                      <td>{c.usedCustomerName || c.customerName || "-"}</td>
                      <td>{c.usedCustomerPhone || c.customerPhone || "-"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </>}
      </div>
    </Layout>
  );
}
